import { defineStore } from "pinia";
import type {
  ProductMappingChecksInput,
  ProductMappingChecksPreview,
  ProductMappingChecksResult,
} from "@price-rescue/api-contracts";
import { PRODUCT_MAPPING_CHECK_OUTCOMES } from "@price-rescue/domain/product-mapping-checks";
import { MerchantApiError } from "../authentication/errors.js";
import { freezeProductBulkSelection } from "./product-bulk-selection.js";
import type {
  ProductMappingChecksService,
  MappingCheckJobProgress,
} from "./product-mapping-checks.js";

export const useProductMappingChecksStore = defineStore("product-mapping-checks", {
  state: (): {
    service?: ProductMappingChecksService;
    selection: string[];
    preview?: ProductMappingChecksPreview;
    result?: ProductMappingChecksResult;
    jobs: Record<string, MappingCheckJobProgress>;
    error?: string;
    submitting: boolean;
    needsRefresh: boolean;
    pendingRequest?: { input: ProductMappingChecksInput; idempotencyKey: string };
  } => ({ selection: [], jobs: {}, submitting: false, needsRefresh: false }),
  actions: {
    configure(service: ProductMappingChecksService): void {
      this.service = service;
    },
    async prepare(productIds: string[]): Promise<void> {
      if (this.submitting || this.needsRefresh) return;
      delete this.error;
      delete this.preview;
      delete this.result;
      this.jobs = {};
      this.submitting = true;
      try {
        this.selection = freezeProductBulkSelection(productIds);
        if (!this.service) throw new Error("Mapping checks are unavailable.");
        this.preview = await this.service.preview({ productIds: [...this.selection] });
      } catch (error: unknown) {
        this.error = error instanceof Error ? error.message : "Could not preview mapping checks.";
      } finally {
        this.submitting = false;
      }
    },
    async execute(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting || this.needsRefresh || !this.preview || !this.service) return;
      this.pendingRequest = {
        input: { productIds: [...this.selection] },
        idempotencyKey: crypto.randomUUID(),
      };
      await this.reconcile(refresh);
    },
    async reconcile(refresh: () => Promise<void>): Promise<void> {
      if (this.submitting) return;
      const wasUncertain = this.needsRefresh && this.pendingRequest !== undefined;
      this.submitting = true;
      delete this.error;
      try {
        if (this.pendingRequest) {
          if (!this.service) throw new Error("Check reconciliation is unavailable.");
          const pending = this.pendingRequest;
          this.result = await this.service.execute(pending.input, pending.idempotencyKey);
          if (!this.result.unconfirmed.length) delete this.pendingRequest;
        }
        delete this.preview;
        this.needsRefresh = true;
        const progressFailed = await this.refreshJobProgress();
        await this.refreshProducts(refresh);
        if (progressFailed && !this.needsRefresh)
          this.error =
            "Some check statuses could not be refreshed. Last reported outcomes remain visible.";
      } catch (error: unknown) {
        this.error =
          error instanceof Error ? error.message : "Mapping check submission is unconfirmed.";
        const definite =
          error instanceof MerchantApiError &&
          !error.retryable &&
          !["network", "server", "unknown"].includes(error.kind);
        if (definite && !wasUncertain) delete this.pendingRequest;
        this.needsRefresh = !definite || wasUncertain;
        if (this.needsRefresh && !this.result) {
          this.result = {
            queued: [],
            existing: [],
            failed: [],
            skipped: [],
            unconfirmed: this.selection.map((productId) => ({
              productId,
              code: PRODUCT_MAPPING_CHECK_OUTCOMES.UNCONFIRMED,
              message: "A check may have started. Reconcile this submission before another batch.",
            })),
          };
          delete this.preview;
        }
      } finally {
        this.submitting = false;
      }
    },
    async refreshJobProgress(): Promise<boolean> {
      const service = this.service;
      const ids = [
        ...new Set(
          [...(this.result?.queued ?? []), ...(this.result?.existing ?? [])].flatMap((item) =>
            item.jobId ? [item.jobId] : [],
          ),
        ),
      ];
      if (!service) return ids.length > 0;
      let failed = false;
      for (let offset = 0; offset < ids.length; offset += 4) {
        await Promise.all(
          ids.slice(offset, offset + 4).map(async (jobId) => {
            try {
              this.jobs[jobId] = await service.getJob(jobId);
            } catch {
              failed = true;
            }
          }),
        );
      }
      return failed;
    },
    async refreshProducts(refresh: () => Promise<void>): Promise<void> {
      try {
        await refresh();
        this.needsRefresh = this.pendingRequest !== undefined;
        if (this.needsRefresh)
          this.error =
            "The original check submission is unconfirmed. Refresh its results before another batch.";
      } catch {
        this.needsRefresh = true;
        this.error = "Refresh products and review check results before another batch.";
      }
    },
  },
});
